// Task modal look & AI voice fill (jazzed-up header, pill pickers, "Speak it" → bullets + tick-to-apply suggestions).
import { useState } from "react";
import { toast } from "sonner";
import { ListChecks, SearchCheck, FileBarChart, LineChart, ShieldCheck, Radar, CalendarDays, Sparkles, Loader2, Check, Lightbulb, Wand2 } from "lucide-react";
import api from "../../../lib/api";
import VoiceNoteButton from "../../quotes/panels/VoiceNoteButton";
import { inkMap } from "../_shared";

export const TYPE_META = inkMap({
  task:     { icon: ListChecks,   hex: "#38bdf8", label: "Task" },
  check:    { icon: SearchCheck,  hex: "#2fc8cb", label: "Check" },
  report:   { icon: FileBarChart, hex: "#a78bfa", label: "Report" },
  analysis: { icon: LineChart,    hex: "#818cf8", label: "Analysis" },
  approval: { icon: ShieldCheck,  hex: "#f59e0b", label: "Approval" },
  monitor:  { icon: Radar,        hex: "#34d399", label: "Monitor" },
  meeting:  { icon: CalendarDays, hex: "#f472b6", label: "Meeting" },
});
const STATUS_HEX = inkMap({ todo: "#94a3b8", in_progress: "#38bdf8", waiting: "#f59e0b", blocked: "#fb7185", done: "#34d399", cancelled: "#64748b" });
const PRI_HEX = inkMap({ low: "#64748b", medium: "#38bdf8", high: "#f59e0b", urgent: "#fb7185" });

const pillStyle = (on, hex) => on ? { background: `${hex}22`, borderColor: `${hex}99`, color: hex, boxShadow: `0 0 0 1px ${hex}33 inset` } : undefined;

export function TypePills({ types, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="task-drawer-type">
      {types.map((t) => { const m = TYPE_META[t.key] || TYPE_META.task; const Icon = m.icon; const on = value === t.key; return (
        <button key={t.key} type="button" onClick={() => onChange(t.key)} style={pillStyle(on, m.hex)} data-testid={`task-type-${t.key}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[12px] transition-[background-color,border-color,transform] active:scale-95 ${on ? "font-semibold" : "border-white/10 text-slate-400 hover:text-white hover:border-white/25"}`}>
          <Icon className="w-3.5 h-3.5" />{t.label}
        </button>
      ); })}
    </div>
  );
}

export function StatusPills({ statuses, value, onChange }) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border border-white/10 overflow-hidden" data-testid="task-drawer-status">
      {statuses.map((s) => { const on = value === s.key; const hex = STATUS_HEX[s.key] || "#94a3b8"; return (
        <button key={s.key} type="button" onClick={() => onChange(s.key)} data-testid={`task-status-pill-${s.key}`}
          style={on ? { background: `${hex}26`, color: hex } : undefined}
          className={`px-2.5 py-1.5 text-[11.5px] border-r border-white/[0.06] last:border-r-0 inline-flex items-center gap-1.5 transition-colors ${on ? "font-semibold" : "text-slate-400 hover:text-white hover:bg-white/[0.04]"}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: hex }} />{s.label}
        </button>
      ); })}
    </div>
  );
}

export function PriorityPills({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="task-drawer-priority">
      {["low", "medium", "high", "urgent"].map((p) => { const on = value === p; return (
        <button key={p} type="button" onClick={() => onChange(p)} style={pillStyle(on, PRI_HEX[p])} data-testid={`task-priority-${p}`}
          className={`px-2.5 py-1 rounded-full border text-[11.5px] capitalize transition-colors ${on ? "font-semibold" : "border-white/10 text-slate-400 hover:text-white"}`}>{p}</button>
      ); })}
    </div>
  );
}

export function EffortPills({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="task-drawer-effort">
      {[["S", "Small"], ["M", "Medium"], ["L", "Large"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(value === k ? null : k)} data-testid={`task-effort-${k}`}
          className={`px-2.5 py-1 rounded-full border text-[11.5px] transition-colors ${value === k ? "bg-teal-500/15 border-teal-400/60 text-teal-200 font-semibold" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
}

export function Switch({ on, onChange, label, sub, testid }) {
  return (
    <button type="button" onClick={() => onChange(!on)} data-testid={testid} className="flex items-center gap-2.5 text-left group">
      <span className={`relative w-9 h-5 rounded-full transition-colors ${on ? "bg-teal-500" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </span>
      <span><span className="block text-[12.5px] text-slate-200">{label}</span>{sub && <span className="block text-[10.5px] text-slate-500">{sub}</span>}</span>
    </button>
  );
}

const LABEL = { title: "Title", description: "Description", type: "Type", priority: "Priority", due_date: "Due date", due_time: "Due time",
  assignee_id: "Owner", team_id: "Team", checklist: "Subtasks", bullet_notes: "Add key points to description", recurrence: "Repeat", ack_required: "Weekly check-in", tags: "Tags" };
const show = (k, v, names) => {
  if (k === "assignee_id") return names.assignee || v;
  if (k === "team_id") return names.team || v;
  if (k === "checklist") return v.map((c) => c.text).join(" · ");
  if (k === "bullet_notes") return `${v.length} bullet${v.length === 1 ? "" : "s"}`;
  if (k === "recurrence") return `${v.rule}${v.rule === "weekly" ? ` (${v.days.map((d) => ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d]).join(", ")})` : v.rule === "monthly" ? ` (day ${v.month_day})` : ""}`;
  if (k === "ack_required") return v ? "On" : "Off";
  if (k === "tags") return v.join(", ");
  if (k === "type") return TYPE_META[v]?.label || v;
  return String(v);
};

export function VoiceFill({ task, types, onApply }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [pick, setPick] = useState({});
  const draft = async (said) => {
    const t = (said ?? text).trim();
    if (!t) { toast.error("Say or type what the task is first"); return; }
    setBusy(true);
    try {
      const current = { title: task.title, description: task.description, type: task.type, priority: task.priority, due_date: task.due_date, due_time: task.due_time,
        assignee_id: task.assignee_id, team_id: task.team_id, tags: task.tags, ack_required: task.ack_required || undefined, recurrence: task.recurrence,
        checklist: (task.checklist || []).map((c) => c.text) };
      const { data } = await api.post("/workspace/ai/task-draft", { text: t, current, types }, { timeout: 90000 });
      const sugg = { ...(data.suggestions || {}) };
      if ((data.bullets || []).length) sugg.bullet_notes = data.bullets;
      setRes({ ...data, said: t, suggestions: sugg }); setPick(Object.fromEntries(Object.keys(sugg).map((k) => [k, true])));
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); } finally { setBusy(false); }
  };
  const apply = () => {
    const s = res?.suggestions || {};
    const have = new Set((task.checklist || []).map((c) => c.text.trim().toLowerCase()));
    const patch = Object.fromEntries(Object.entries(s).filter(([k]) => pick[k] && k !== "bullet_notes").map(([k, v]) => [k, k === "checklist" ? [...(task.checklist || []), ...v.filter((c) => !have.has(c.text.trim().toLowerCase()))] : v]));
    if (pick.bullet_notes && s.bullet_notes?.length) {
      const base = (patch.description ?? task.description ?? "").trim();
      patch.description = [base, "Notes:", ...s.bullet_notes.map((b) => `• ${b}`)].filter(Boolean).join("\n");
    }
    if (!Object.keys(patch).length) { toast("Nothing ticked"); return; }
    onApply(patch); toast.success(`Applied ${Object.keys(patch).length} suggestion${Object.keys(patch).length === 1 ? "" : "s"}`); setRes(null); setText("");
  };
  return (
    <div className="rounded-xl border border-violet-400/30 bg-gradient-to-br from-violet-500/[0.10] via-violet-500/[0.04] to-transparent p-3" data-testid="task-voice-fill">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-violet-300" />
        <span className="text-[12.5px] font-semibold text-violet-100">Speak it — AI drafts the task</span>
        <span className="text-[10.5px] text-violet-300/70 hidden sm:inline">listens, summarises, suggests fields you tick to apply</span>
        <div className="ml-auto flex gap-1.5 shrink-0">
          <VoiceNoteButton url="/ai/transcribe" testid="task-voice-record" label="Speak it" title="Talk through the task — AI transcribes and drafts it"
            onResult={async (d) => { const said = d.clean || d.raw || ""; setText(said); await draft(said); }} />
          <button type="button" onClick={() => draft()} disabled={busy || !text.trim()} data-testid="task-ai-fill"
            className="inline-flex items-center gap-1 px-2.5 h-[30px] whitespace-nowrap rounded-lg border border-violet-400/40 text-violet-200 text-[11px] font-bold hover:bg-violet-500/15 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}{busy ? "Thinking…" : "AI fill"}
          </button>
        </div>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={text ? 3 : 2} placeholder="…or type rough notes — e.g. 'Every Monday Mari checks parked orders over £3k, high priority, steps: review, fix margin, notify Aiste'"
        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-[12.5px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-violet-400/60 resize-y" data-testid="task-voice-text" />
      {res && (
        <div className="mt-3 grid md:grid-cols-2 gap-3" data-testid="task-voice-result">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-violet-300 mb-1">Key points</div>
            <ul className="space-y-1" data-testid="task-voice-bullets">{(res.bullets || []).map((b, i) => <li key={i} className="text-[12px] text-slate-200 flex gap-1.5"><span className="text-violet-300">•</span>{b}</li>)}</ul>
            {res.said && (
              <details className="mt-2" data-testid="task-voice-transcript">
                <summary className="text-[10px] uppercase tracking-widest font-mono text-violet-300/80 cursor-pointer">Full transcript</summary>
                <p className="mt-1 text-[11.5px] text-slate-300 whitespace-pre-wrap bg-black/20 rounded-md p-2 max-h-40 overflow-y-auto">{res.said}</p>
              </details>
            )}
            {(res.tips || []).length > 0 && <div className="mt-2 space-y-1" data-testid="task-voice-tips">{res.tips.map((t, i) => <div key={i} className="text-[11.5px] text-amber-200/90 flex gap-1.5"><Lightbulb className="w-3.5 h-3.5 shrink-0 mt-0.5" />{t}</div>)}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-violet-300 mb-1">Suggested fields — tick to apply</div>
            {Object.keys(res.suggestions || {}).length === 0 && <div className="text-[12px] text-slate-500 flex items-center gap-2">Nothing new to suggest.<button type="button" onClick={() => setRes(null)} className="ml-auto text-slate-400 hover:text-white" data-testid="task-voice-dismiss">Dismiss</button></div>}
            {Object.entries(res.suggestions || {}).map(([k, v]) => (
              <label key={k} className="flex items-start gap-2 py-1 cursor-pointer" data-testid={`task-voice-sugg-${k}`}>
                <input type="checkbox" checked={!!pick[k]} onChange={() => setPick((p) => ({ ...p, [k]: !p[k] }))} className="accent-violet-500 mt-0.5" />
                <span className="text-[12px] min-w-0"><span className="text-violet-200 font-medium">{LABEL[k] || k}: </span><span className="text-slate-200 break-words">{show(k, v, res.names || {})}</span></span>
              </label>
            ))}
            {Object.keys(res.suggestions || {}).length > 0 && (
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => setRes(null)} className="px-2.5 py-1 rounded text-[11.5px] text-slate-400 hover:text-white" data-testid="task-voice-dismiss">Dismiss</button>
                <button type="button" onClick={apply} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-[12px] font-semibold" data-testid="task-voice-apply"><Check className="w-3.5 h-3.5" />Apply selected</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
